import type {
    DoseId,
    ImageId,
    RegistrationId,
    StructureSetId,
} from '../changes/ids';
import type {
    ConnectorVersionToken,
    DatasetPersistenceRef,
    PatientPersistenceRef,
} from './PersistenceRef';

/**
 * Ids that can carry a connector-database identity. All branded string
 * UUIDs minted per session, so one map serves every kind without
 * collisions.
 */
export type PersistedEntityId =
    | StructureSetId
    | RegistrationId
    | ImageId
    | DoseId;

/**
 * Read-only view of the registry — all a dataset saver may hold: savers
 * READ refs for retry-safety decisions, but every write travels back to
 * the orchestrator as a returned {@link SaveEffect}. Holding this type
 * instead of the registry makes a stray write a compile error, the same
 * enforcement the sealed inputs provide for the aggregate.
 */
export interface PersistenceRefReader {
    getRef(id: PersistedEntityId): DatasetPersistenceRef | null;
}

/**
 * Session-scoped map from client-local dataset ids to their connector-next
 * database identity.
 *
 * Owned by the {@link PatientSession}, not stored on the entities: the save
 * workflow must still know the database identity of a dataset the user has
 * deleted locally (to issue the DELETE), which an entity-held ref could not
 * provide once the entity is gone.
 *
 * A dataset without an entry is "local only": loaded from local files,
 * unmatched during hydration, or created in the UI and not yet saved.
 */
export class PersistenceRegistry implements PersistenceRefReader {
    private readonly refs = new Map<PersistedEntityId, DatasetPersistenceRef>();

    constructor(
        /**
         * The loaded patient's database identity, or null when the session
         * has no connector backing (local files) — in which case no
         * dataset refs exist either and saving is unavailable.
         */
        public readonly patientRef: PatientPersistenceRef | null
    ) {}

    getRef(id: PersistedEntityId): DatasetPersistenceRef | null {
        return this.refs.get(id) ?? null;
    }

    setRef(id: PersistedEntityId, ref: DatasetPersistenceRef): void {
        this.refs.set(id, ref);
    }

    /**
     * Records the token returned by a successful save. Throws for an
     * unregistered id: updating a version for an object whose database
     * identity is unknown means the save bookkeeping is broken, and
     * continuing would silently disable conflict detection.
     */
    updateVersion(id: PersistedEntityId, version: ConnectorVersionToken): void {
        const existing = this.refs.get(id);
        if (!existing) {
            throw new Error(
                'PersistenceRegistry.updateVersion: dataset has no ' +
                    'persistence ref'
            );
        }
        this.refs.set(id, { datasetId: existing.datasetId, version });
    }

    removeRef(id: PersistedEntityId): void {
        this.refs.delete(id);
    }

    /** Number of registered dataset refs. */
    get size(): number {
        return this.refs.size;
    }
}
